import { defaultRequiredFields } from '@onward-delivery/core';
import { uniq } from 'lodash';
import { hasPhoneWarning } from '@/utilities/validatePhoneNumber';
import { getInvalidServiceLines } from '../../../utilities/serviceCatalog';
import { middleMileOriginFields } from '../../../hooks/modal';
import { EDIT_ORDER_TABS } from '../../../constants';
import { getItemErrors } from '../tabs/ItemsEditTab/utils';
import { isReviewGeocodingStatus } from './geocoding';

const PRICE_OVERRIDE_FIELDS = [
    'admin_shipper_rate_override',
    'admin_carrier_rate_override',
    'admin_middle_mile_shipper_rate_override',
    'admin_middle_mile_carrier_rate_override',
    'order_revenue_adjustments',
];

/**
 * The order fields that must be filled before Done works.
 *
 * Uses `requiredAttributes` (a list, or a function of the draft) when the caller gave one, else the
 * core defaults, then adds the middle-mile origin fields when the ORIGIN tab is shown.
 *
 * @param {object} clone The draft order.
 * @param {object} opts
 * @param {string[]|((order: object) => string[])} [opts.requiredAttributes]
 * @param {boolean} [opts.disableGeocoding]
 * @param {string[]} opts.tabs The tabs the modal shows.
 * @param {object} opts.originAttrs `genAttributes(clone, 'ORIGIN')`.
 * @returns {string[]}
 */
export const requiredFields = (clone, { requiredAttributes, disableGeocoding, tabs, originAttrs }) => {
    let baseRequired;
    if (requiredAttributes) {
        if (Array.isArray(requiredAttributes)) {
            baseRequired = requiredAttributes;
        } else if (typeof requiredAttributes === 'function') {
            baseRequired = requiredAttributes(clone);
        }
    }

    // Keep derived geocode/route requirements aligned with the caller. EditModal has custom
    // validation for each field, so calculated async fields pass when the caller disables
    // geocoding or related address fields are dirty.
    if (baseRequired === undefined) {
        baseRequired = defaultRequiredFields(clone, { disableGeocoding });
    }
    const originRequired =
        clone?.has_middle_mile_origin && tabs.includes(EDIT_ORDER_TABS.ORIGIN)
            ? middleMileOriginFields(originAttrs)
            : [];

    return uniq([...(baseRequired || []), ...originRequired].filter(Boolean));
};

/**
 * Which required fields are invalid, as `{ [field]: true }`.
 *
 * @param {object} clone The draft order.
 * @param {string[]} required From `requiredFields`.
 * @param {object} opts
 * @param {string[]} opts.tabs The tabs the modal shows.
 * @param {object} opts.isDirty Field dirty flags.
 * @param {object} opts.originAttrs `genAttributes(clone, 'ORIGIN')`.
 * @param {boolean} [opts.disableGeocoding]
 * @returns {Object<string, true>}
 */
export const fieldErrors = (clone, required, { tabs, isDirty, originAttrs, disableGeocoding }) => {
    if (!clone) return {};

    return Object.fromEntries(
        required
            .filter((field) => {
                const isEmpty = !clone[field] || clone[field].length === 0;
                switch (field) {
                    case 'dropoff_lat':
                    case 'dropoff_lng':
                        if (
                            clone.order_type === 'will_call' ||
                            (!tabs.includes(EDIT_ORDER_TABS.PICKUP) && clone.order_type === 'return')
                        ) {
                            return false;
                        }
                        if (clone.dropoff_address_is_custom) {
                            return isEmpty;
                        }
                        if (clone.dropoff_address_geocode_failed) {
                            return !isDirty.dropoff_address;
                        }
                        // Saved geocoded orders need coords
                        return clone.order_id ? isEmpty : false;
                    case 'pickup_lat':
                    case 'pickup_lng':
                        if (!tabs.includes(EDIT_ORDER_TABS.PICKUP) && clone.order_type !== 'return') {
                            return false;
                        }
                        if (clone.pickup_address_is_custom) {
                            return isEmpty;
                        }
                        if (clone.pickup_address_geocode_failed) {
                            return !isDirty.pickup_address;
                        }
                        // Saved geocoded orders need coords
                        return clone.order_id ? isEmpty : false;
                    case originAttrs.lat:
                    case originAttrs.long:
                        if (!clone.has_middle_mile_origin || !tabs.includes(EDIT_ORDER_TABS.ORIGIN)) {
                            return false;
                        }
                        if (isReviewGeocodingStatus(clone.geocoding?.[originAttrs.geocoding]?.status)) {
                            return true;
                        }
                        return clone.order_id ? isEmpty : false;
                    case 'shipper_rate':
                    case 'carrier_rate':
                        return false;
                    case 'itemsByOrderId':
                        return clone.order_type === 'service' ? false : isEmpty;
                    case 'duration_seconds':
                    case 'distance':
                    case 'miles':
                        if (clone.order_type === 'will_call') {
                            return false;
                        }
                        if (clone.distance_failed || clone[field] === 0) {
                            return !(isDirty.pickup_address || isDirty.dropoff_address);
                        }
                        // distance/miles/duration_seconds are derived route metrics
                        // recomputed by the backend on save. In disableGeocoding mode
                        // they can't be fixed client-side, so flagging would lock Save
                        // forever — skip the check then.
                        return disableGeocoding ? false : isEmpty;
                    // Phone never blocks Save. An unparseable number surfaces through
                    // phoneWarnings so it is visible without locking the order.
                    case 'pickup_phone':
                    case 'dropoff_phone':
                        return false;
                    case 'preferred_delivery_date':
                    case 'alternative_delivery_dates':
                        return false;
                    case 'warehouse_delivery_status':
                    case 'warehouse_estimated_delivery_date':
                        return clone.is_middle_mile && isEmpty;

                    default:
                        return isEmpty;
                }
            })
            .map((field) => [field, true])
    );
};

/**
 * Non-blocking counterpart to `fieldErrors`: every `*_phone` field the order carries whose value is
 * present but unparseable, as `{ [field]: true }`.
 *
 * @param {object} clone The draft order.
 * @returns {Object<string, true>}
 */
export const phoneWarnings = (clone) => {
    if (!clone) return {};

    return Object.fromEntries(
        Object.keys(clone)
            .filter((field) => field.endsWith('_phone') && hasPhoneWarning(clone[field]))
            .map((field) => [field, true])
    );
};

/**
 * Per-item errors from the same helper each item row uses. One entry per item, in order.
 *
 * @param {object} clone The draft order.
 * @returns {object[]}
 */
export const itemErrors = (clone) => {
    if (!clone) return [];

    return (clone.itemsByOrderId || []).map((item) =>
        getItemErrors(item, {
            oms: clone.oms,
            freightType: clone.freight_type,
            sourceForm: clone.source_form,
            assemblyRequired: clone.assembly_required,
        })
    );
};

/**
 * Admin-tab errors: date and window order, and a rate override changed without an Invoicing note.
 * `{}` when neither admin tab is shown.
 *
 * A rate override counts as the user's change only when it differs from `base`, the stored order
 * the draft started from. After **Reload order** that is the fresh order, so a rate someone else
 * changed does not demand a note.
 *
 * @param {object} args
 * @param {object} args.clone The draft order.
 * @param {object} args.overrides The draft's overrides (edited fields).
 * @param {object} args.base The stored order edits are measured against (`baseOrder`).
 * @param {string[]} args.tabs The tabs the modal shows.
 * @returns {Object<string, boolean>}
 */
export const adminErrors = ({ clone, overrides, base, tabs }) => {
    const isAdmin = tabs.includes(EDIT_ORDER_TABS.ADMIN);
    const isAdminRates = tabs.includes(EDIT_ORDER_TABS.ADMIN_RATES);

    if (!isAdmin && !isAdminRates) return {};

    const priceOverrideChanged = PRICE_OVERRIDE_FIELDS.some(
        (attr) => !!overrides?.[attr] && overrides?.[attr] !== base?.[attr]
    );
    const hasNewInvoicingNote = (clone?.notes || []).some((n) => !n?.created_at && n?.type === 'Invoicing');
    const rate_change_note = priceOverrideChanged && !hasNewInvoicingNote;

    if (!isAdmin) return { rate_change_note };

    const dropoffBeforePickup =
        clone.del_window_start &&
        clone.pickup_window_end &&
        new Date(clone.del_window_start) < new Date(clone.pickup_window_start);

    return {
        delivery_date: dropoffBeforePickup,
        pickup_date: dropoffBeforePickup,
        delivery_window:
            clone.del_window_start &&
            clone.del_window_end &&
            new Date(clone.del_window_start) > new Date(clone.del_window_end),
        pickup_window:
            clone.pickup_window_start &&
            clone.pickup_window_end &&
            new Date(clone.pickup_window_start) > new Date(clone.pickup_window_end),
        rate_change_note,
    };
};

/**
 * True when a service line carries a code the catalog doesn't offer. Not dirty-gated, so an invalid
 * code reads red on open.
 *
 * @param {object} clone The draft order.
 * @param {object} serviceCatalog The loaded catalog.
 * @returns {boolean}
 */
export const serviceErrors = (clone, serviceCatalog) => getInvalidServiceLines(clone, serviceCatalog).length > 0;

/**
 * True when a reference has a blank key or value, or two references share a key.
 *
 * @param {object} clone The draft order.
 * @returns {boolean}
 */
export const referenceErrors = (clone) => {
    const refs = clone?.references || [];
    const trimmedKeys = refs.map((r) => (r.key || '').trim()).filter(Boolean);
    const hasBlank = refs.some((r) => !(r.key || '').trim() || !(r.value || '').trim());
    const hasDupKey = new Set(trimmedKeys).size !== trimmedKeys.length;
    return hasBlank || hasDupKey;
};

/**
 * Which tabs show the error mark, as `{ [tab]: boolean }`.
 *
 * A field error counts when `startDirty` is set or the field is dirty. The admin note, services, and
 * references errors land on their own tabs regardless of dirty state.
 *
 * @param {object} args
 * @param {Object<string, string[]>} args.fieldsByTab Order fields each tab renders.
 * @param {object} args.hasError From `fieldErrors`.
 * @param {object[]} args.itemHasError From `itemErrors`.
 * @param {object} args.isDirty Field dirty flags.
 * @param {object} args.adminHasError From `adminErrors`.
 * @param {boolean} args.serviceHasError From `serviceErrors`.
 * @param {boolean} args.referenceHasError From `referenceErrors`.
 * @param {boolean} [args.startDirty]
 * @returns {Object<string, boolean>}
 */
export const errorsByTab = ({
    fieldsByTab,
    hasError,
    itemHasError,
    isDirty,
    adminHasError,
    serviceHasError,
    referenceHasError,
    startDirty,
}) => {
    const fieldFlagged = (tab) => fieldsByTab[tab].some((field) => hasError[field] && (startDirty || isDirty[field]));

    const errorObject = Object.fromEntries(
        Object.keys(fieldsByTab).map((tab) => {
            if (tab === EDIT_ORDER_TABS.ITEMS) {
                return [
                    tab,
                    fieldFlagged(tab) ||
                        itemHasError.some((item, idx) =>
                            Object.entries(item).some(
                                ([key, isInvalid]) => isInvalid && (startDirty || isDirty.itemsByOrderId?.[idx]?.[key])
                            )
                        ),
                ];
            }
            return [tab, fieldFlagged(tab)];
        })
    );

    if (adminHasError.rate_change_note) {
        errorObject[EDIT_ORDER_TABS.ADMIN_NOTES] = true;
    }
    // Always flag SERVICES: that's where the invalid line renders, though genAttributesPerTab
    // buckets the services field under ITEMS.
    if (serviceHasError) {
        errorObject[EDIT_ORDER_TABS.SERVICES] = true;
    }
    // References render under the ORDER_DETAILS tab.
    if (referenceHasError) {
        errorObject[EDIT_ORDER_TABS.ORDER_DETAILS] = true;
    }

    return errorObject;
};

/**
 * True when nothing blocks Done: no field, item, admin, service, or reference error.
 *
 * @param {object} errs
 * @returns {boolean}
 */
export const allValid = ({ hasError, itemHasError, adminHasError, serviceHasError, referenceHasError }) =>
    !Object.values(hasError).some(Boolean) &&
    !Object.values(itemHasError).some((item) => Object.values(item).some(Boolean)) &&
    !Object.values(adminHasError).some(Boolean) &&
    !serviceHasError &&
    !referenceHasError;
